/* eslint-disable complexity */
import type { MaterialClassEntity } from './material-class.entity';
import type { PendingMediaObjectReference } from '@redviking/argonaut-util/src/media.util';
import type { PendingMediaStoreState } from 'src/components/EntityDetail/entity-detail.store';
import type { ValidationResult } from 'types';
import type { MediaUploadDialogPayload } from '../media/MediaUploadDialog.vue';

export const validateMaterialClass = (entity: MaterialClassEntity): ValidationResult => {
    if (!entity.materialClass.name || entity.materialClass.name.length <= 0) {
        return {
            status: 'error',
            routeName: 'materialClassSettings',
            errorMessage: 'Material Class missing "name" property',
        };
    }

    for (const udfSchema of entity.materialClass.udf_schemas) {
        if (!udfSchema.udf_column.name) {
            return {
                status: 'error',
                routeName: 'materialClassSettings',
                errorMessage: 'Material Class missing "name" property in UDF Schema',
            };
        }
        if (entity.materialClass.udf_schemas.find(otherUdfSchema => otherUdfSchema.udf_column.name === udfSchema.udf_column.name && udfSchema.udf_column.id !== otherUdfSchema.udf_column.id)) {
            return {
                status: 'error',
                routeName: 'materialClassSettings',
                errorMessage: `Material Class UDF Schema "${udfSchema.udf_column.name}" already exists`,
            };
        }
    }

    for (const udfClassDefaultData of Object.values(entity.materialClass.meta.udfDefaultData || {})) {
        const value = udfClassDefaultData.num_val ?? udfClassDefaultData.str_val ?? null;
        const udfSchema = entity.materialClass.udf_schemas.find(udfSchema => udfSchema.udf_column.id === udfClassDefaultData.udf_column_id);
        if (!udfSchema) {
            return {
                status: 'error',
                routeName: 'materialClassDataItemList',
                errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" not found, but a value was provided`,
            };
        }
        if (!entity.ignoreWarnings && udfSchema.udf_column.validation_required && value === null) {
            return {
                status: 'modal',
                modalComponent: () => import(/* webpackChunkName: "material" */ './MaterialClassSaveWarning.vue').then(m => m.default),
            };
        }
        if (typeof udfSchema.udf_column.validation_min === 'number' && value !== null) {
            if (typeof value !== 'number') {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected a number, received ${typeof value}. Value: ${value}`,
                };
            } else if (value < udfSchema.udf_column.validation_min) {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected min value ${udfSchema.udf_column.validation_min}, received ${value}`,
                };
            }
        }
        if (udfSchema.udf_column.validation_max && value !== null) {
            if (typeof value !== 'number') {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected a number, received ${typeof value}. Value: ${value}`,
                };
            } else if (value > udfSchema.udf_column.validation_max) {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected max value ${udfSchema.udf_column.validation_max}, received ${value}`,
                };
            }
        }
        if (typeof udfSchema.udf_column.validation_pattern === 'string' && value !== null) {
            if (typeof value !== 'string') {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected a string, received ${typeof value}. Value: ${value}`,
                };
            } else if (!new RegExp(`^${udfSchema.udf_column.validation_pattern}$`, 'u').test(value)) {
                return {
                    status: 'error',
                    routeName: 'materialClassDataItemList',
                    errorMessage: `Material Class UDF Schema "${udfClassDefaultData.udf_column_id}" expected pattern value ${udfSchema.udf_column.validation_pattern}, received ${value}`,
                };
            }
        }
    }

    const classPendingMedia = {
        pfp: entity.pendingProfilePictureMedia,
        modelPendingMedia: entity.pendingModelLinkedMedia,
        classMedia: entity.pendingClassLinkedMedia,
    };

    
    const pendingMedia = [ ...Object.values(classPendingMedia.modelPendingMedia || {}).flatMap(m => m).concat(classPendingMedia.classMedia), classPendingMedia.pfp ]
        .filter((m): m is PendingMediaObjectReference => Boolean(m)); // just to cull a null pfp
    if (pendingMedia.length) {
        return {
            status: 'modal',
            modalComponent: () => import(/* webpackChunkName: "material" */ '../media/MediaUploadDialog.vue').then(m => m.default),
            modalProps: {
                pendingMedia,
                updateEntity: (payload: MediaUploadDialogPayload) => {
                    const updatedMediaItemIds: string[] = [];
                    const tempEntity = payload.accessor.entity as {
                        type: 'materialclass';
                        entity: MaterialClassEntity;
                        pendingMedia: PendingMediaStoreState;
                    };
                    payload.savedMedia.forEach(media => {
                        if (classPendingMedia.pfp && media.file.id === classPendingMedia.pfp.id) {
                            tempEntity.entity.materialClass.linked_profile_picture = media;
                            tempEntity.entity.pendingProfilePictureMedia = null;
                            updatedMediaItemIds.push(media.file.id);
                        }
                        if (classPendingMedia.classMedia.find(m => m.id === media.file.id)) {
                            tempEntity.entity.materialClass.linked_media = tempEntity.entity.materialClass.linked_media.concat(media);
                            tempEntity.entity.pendingClassLinkedMedia = tempEntity.entity.pendingClassLinkedMedia.filter(m => m.id !== media.file.id);
                            updatedMediaItemIds.push(media.file.id);
                        }
                        if (Object.values(classPendingMedia.modelPendingMedia || {}).flatMap(e => e).length) {
                            const newModelPendingMedia = Object.entries(classPendingMedia.modelPendingMedia).reduce((acc, [ modelId, mediaList ]) => {
                                acc[modelId] = mediaList.map(m => m.id === media.file.id ? null : m).filter(e => e && !updatedMediaItemIds.includes(e.id)) as PendingMediaObjectReference[];
                                if (acc[modelId].length === 0) {
                                    delete acc[modelId];
                                }
                                return acc;
                            }, {} as Record<string, PendingMediaObjectReference[]>);
                            tempEntity.entity.materialClass.material_models = tempEntity.entity.materialClass.material_models.map(mm => {
                                if (classPendingMedia.modelPendingMedia?.[mm.modelCfg.id].find(m => m.id === media.file.id)) {
                                    return {
                                        ...mm,
                                        modelCfg: {
                                            ...mm.modelCfg,
                                            linked_media: mm.modelCfg.linked_media.concat(media),
                                        },
                                    };
                                }
                                return mm;
                            });
                            tempEntity.entity.pendingModelLinkedMedia = newModelPendingMedia;
                            updatedMediaItemIds.push(media.file.id);
                        }
                    });
                    payload.accessor.setPageEntity(tempEntity);
                },
            },
        };
    }

    for (const setting of entity.materialClass.material_class_attribute_settings) {
        if (!setting.config) {
            return {
                status: 'error',
                routeName: 'materialClassAttributeSettings',
                errorMessage: 'Material Class Attribute Setting missing "config" property',
            };
        }
        if (!setting.config.type) {
            return {
                status: 'error',
                routeName: 'materialClassAttributeSettings',
                errorMessage: 'Material Class Attribute Setting missing "type"',
            };
        }
        if (setting.config.strict === undefined) {
            return {
                status: 'error',
                routeName: 'materialClassAttributeSettings',
                errorMessage: 'Material Class Attribute Setting missing "strict"',
            };
        }
        if (setting.config.type === 'range') {
            if (setting.config.min.value === undefined) {
                return {
                    status: 'error',
                    routeName: 'materialClassAttributeSettings',
                    errorMessage: 'Material Class Attribute Setting of type "range" missing minimum value',
                };
            }
            if (setting.config.max.value === undefined) {
                return {
                    status: 'error',
                    routeName: 'materialClassAttributeSettings',
                    errorMessage: 'Material Class Attribute Setting of type "range" missing maximum value',
                };
            }
            if (typeof setting.config.max.value === 'number' && typeof setting.config.min.value === 'number' && setting.config.max.value < setting.config.min.value) {
                return {
                    status: 'error',
                    routeName: 'materialClassAttributeSettings',
                    errorMessage: 'Material Class Attribute Setting of type "range" maximum value must be greater than minimum value',
                };
            }
        }
    }

    for (const materialModel of entity.materialClass.material_models) {
        // validate material model udf data
        for (const udfItem of materialModel.modelCfg.material_udf_default_data) {
            let classDefault;
            if (entity.materialClass.meta.udfDefaultData && Object.keys(entity.materialClass.meta.udfDefaultData).length) {
                const defaultValue = entity.materialClass.meta.udfDefaultData[udfItem.udf_column_id];
                classDefault = defaultValue && (defaultValue.num_val ?? defaultValue.str_val ?? null);
            }
            // NOTE: Throwing the class default in the value aggregation because if the model value is missing and the class value is set we can use that since that is what will be used during material creation
            const value = udfItem.num_val ?? udfItem.str_val ?? classDefault ?? null;
            const udfSchema = entity.materialClass.udf_schemas.find(udfSchema => udfSchema.udf_column.id === udfItem.udf_column_id);
            if (!udfSchema) {
                return {
                    status: 'error',
                    routeName: 'materialClassSettings',
                    errorMessage: `Material Model "${materialModel.modelCfg.model_number}" provided a material class data item value for a udf schema that does not exist for the material class. Schema id: "${udfItem.id}"`,
                };
            }
            if (udfSchema.udf_column.validation_required && value === null) {
                return {
                    status: 'error',
                    routeName: 'materialClassSettings',
                    errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a value for "${udfSchema.udf_column.name}"`,
                };
            } else if (typeof udfSchema.udf_column.validation_min === 'number' && value !== null) {
                if (typeof value !== 'number') {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a number for "${udfSchema.udf_column.name}"`,
                    };
                } else if (value < udfSchema.udf_column.validation_min) {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a value greater than or equal to ${udfSchema.udf_column.validation_min} for "${udfSchema.udf_column.name}"`,
                    };
                }
            } else if (udfSchema.udf_column.validation_max && value !== null) {
                if (typeof value !== 'number') {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a number for "${udfSchema.udf_column.name}"`,
                    };
                } else if (value > udfSchema.udf_column.validation_max) {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a value less than or equal to ${udfSchema.udf_column.validation_max} for "${udfSchema.udf_column.name}"`,
                    };
                }
            } else if (typeof udfSchema.udf_column.validation_pattern === 'string' && value !== null) {
                if (typeof value !== 'string') {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a string for "${udfSchema.udf_column.name}"`,
                    };
                } else if (!new RegExp(`^${udfSchema.udf_column.validation_pattern}$`, 'u').test(value)) {
                    return {
                        status: 'error',
                        routeName: 'materialClassSettings',
                        errorMessage: `Material Model "${materialModel.modelCfg.model_number}" must provide a value matching ${udfSchema.udf_column.validation_pattern} for "${udfSchema.udf_column.name}"`,
                    };
                }
            }
        }
    }

    return {
        status: 'success',
    };
};
